import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Building, Globe, Mail, MapPin, Users } from 'lucide-react';
import { CompanyMembers } from '@/components/settings/company-members';

export default function CompanySettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedCompany, setEditedCompany] = useState<any>(null);

  useEffect(() => {
    if (user) {
      fetchCompanyDetails();
    }
  }, [user]);

  const fetchCompanyDetails = async () => {
    try {
      // First get the user's primary company
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('primary_company_id')
        .eq('id', user?.id)
        .single();

      if (profileError) throw profileError;
      if (!profile?.primary_company_id) {
        setLoading(false);
        return;
      }

      // Then fetch company details
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', profile.primary_company_id)
        .single();

      if (companyError) throw companyError;
      setCompany(companyData);
      setEditedCompany(companyData);
    } catch (error) {
      console.error('Error fetching company details:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load company details'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!company?.id || !editedCompany) return;

    try {
      const { error } = await supabase
        .from('companies')
        .update({
          name: editedCompany.name,
          description: editedCompany.description,
          website: editedCompany.website,
          email: editedCompany.email,
          headquarters_location: editedCompany.headquarters_location,
          industry: editedCompany.industry,
          size_range: editedCompany.size_range,
          updated_at: new Date().toISOString()
        })
        .eq('id', company.id);

      if (error) throw error;

      setCompany(editedCompany);
      setIsEditing(false);
      toast({
        title: 'Success',
        description: 'Company details updated successfully'
      });
    } catch (error) {
      console.error('Error updating company:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update company details'
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-6">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!company) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Company Associated</h3>
            <p className="text-muted-foreground mb-4">
              You need to set up or join a company to access these settings.
            </p>
            <Button>Set Up Company</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Company Profile */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building className="h-5 w-5 text-primary" />
              <CardTitle>Company Profile</CardTitle>
            </div>
            <Button 
              variant="outline"
              onClick={() => {
                if (isEditing) {
                  handleSave();
                } else {
                  setIsEditing(true);
                }
              }}
            >
              {isEditing ? 'Save Changes' : 'Edit Profile'}
            </Button>
          </div>
          <CardDescription>
            Manage your company information and settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">Company Name</label>
                <Input
                  value={editedCompany.name}
                  onChange={(e) => setEditedCompany({ ...editedCompany, name: e.target.value })}
                />
              </div>

              <div>
                <label className="text-sm font-medium">Description</label>
                <Textarea
                  value={editedCompany.description}
                  onChange={(e) => setEditedCompany({ ...editedCompany, description: e.target.value })}
                  className="min-h-[100px]"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">Website</label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={editedCompany.website}
                      onChange={(e) => setEditedCompany({ ...editedCompany, website: e.target.value })}
                      className="pl-10"
                      placeholder="https://example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={editedCompany.email}
                      onChange={(e) => setEditedCompany({ ...editedCompany, email: e.target.value })}
                      className="pl-10"
                      placeholder="contact@example.com"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">Location</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={editedCompany.headquarters_location}
                    onChange={(e) => setEditedCompany({ ...editedCompany, headquarters_location: e.target.value })}
                    className="pl-10"
                    placeholder="City, Country"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium">{company.name}</h3>
                <p className="text-muted-foreground mt-1">{company.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {company.website && (
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <a 
                      href={company.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {company.website.replace(/^https?:\/\//, '')}
                    </a>
                  </div>
                )}

                {company.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a 
                      href={`mailto:${company.email}`}
                      className="text-primary hover:underline"
                    >
                      {company.email}
                    </a>
                  </div>
                )}
              </div>

              {company.headquarters_location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>{company.headquarters_location}</span>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Team Members */}
      <CompanyMembers companyId={company.id} />
    </div>
  );
}